<template>
  <div
    class="grid-item rounded-2 pa-3 flex flex-col gap-1 cursor-pointer relative"
    :class="{
      'grid-item--selected': isSelected,
      'grid-item--base': !isSelected,
      [`min-w-[264px]`]: item.type === 'folder',
      [`min-h-[170px]`]: item.type === 'file' || item.type === 'page',
      'color-text2': isDisabled,
      'cursor-not-allowed': isDisabled,
    }"
    :draggable="!!selectedIds.length && movable"
    @click="onGridItemClick"
    @dragend="handleDragEnd"
    @dragenter.prevent
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
    @dragstart="handleDragStart"
    @drop="handleDrop"
  >
    <template v-if="item.type === 'file' || item.type === 'page'">
      <div class="absolute top-2 left-2 w-9 h-9 flex items-center justify-center rounded-5">
        <CButton
          circle
          :color="themeVars.primary6"
          :disabled="
            (item.type === 'file' && appProps?.contentType.value === 'slide') ||
            (item.content_type === 'ar' && !isPitcherOrIosDevice)
          "
          :text-color="themeVars.primary"
          @click.stop="emit('itemClicked', item, $event)"
        >
          <CIcon icon="eye" />
        </CButton>
      </div>

      <div
        v-if="item.type === 'file'"
        class="absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-5"
      >
        <CButton
          v-tooltip="
            launchDarkly.enable_file_divisible_permission && item.permissions?.can_be_divided === false
              ? t('canvasUI.files.notDivisibleExpanded')
              : undefined
          "
          circle
          :color="themeVars.primary6"
          :disabled="
            appProps?.contentType.value === 'file' ||
            item.content_type !== 'pdf' ||
            (launchDarkly.enable_file_divisible_permission && item.permissions?.can_be_divided === false)
          "
          :text-color="themeVars.primary"
          @click.stop="emit('itemExpanded', item)"
        >
          <CIcon icon="angle-right" />
        </CButton>
      </div>

      <CImage
        class="rounded-2"
        height="135"
        :img-props="{
          draggable: false,
        }"
        lazy
        object-fit="cover"
        preview-disabled
        :src="item.thumbnail_url"
        width="240"
      />
      <div class="text-truncate max-w-[240px] font-700 text-center">
        {{ item.name }}
      </div>
      <div class="flex items-center justify-between w-full">
        <div class="text-truncate text-center" :style="{ color: themeVars.text3 }">
          {{ item.content_type?.toUpperCase() }}
        </div>
        <CInfoBadge
          v-if="!isShareable"
          v-tooltip="t('canvasUI.components.ContentGrid.sharingDisabled')"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          variation="minimal"
        />
      </div>
    </template>
    <div v-else-if="item.type === 'folder'" class="max-w-[240px] flex items-center">
      <CImage
        broken-image-size="16px"
        class="rounded-2"
        height="50"
        lazy
        object-fit="cover"
        preview-disabled
        :src="item.thumbnail_url"
        width="50"
      />
      <div class="grid-item__folder-center truncate flex-1 ml-2">
        {{ item.name }}
      </div>
    </div>
    <template v-else-if="item.type === 'asset'">
      <div class="absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-5 bg-base">
        <CButton
          v-tooltip="isDeleteDisabled ? deleteDisabledTooltip : ''"
          circle
          :disabled="!canDelete || isDeleteDisabled"
          secondary
          type="error"
          @click.stop="emit('itemDeleted', item)"
        >
          <template #icon>
            <CIcon icon="trash" size="16px" />
          </template>
        </CButton>
      </div>
      <CImage
        class="rounded-2"
        height="135"
        :img-props="{
          draggable: false,
        }"
        lazy
        object-fit="cover"
        preview-disabled
        :src="item.thumbnail_url"
        width="240"
      />
      <div class="text-truncate max-w-[240px] font-700 text-center">
        {{ item.name }}
      </div>
      <div class="text-truncate text-center" :style="{ color: themeVars.text3 }">
        {{ item.content_extension?.toUpperCase() }}
      </div>
    </template>

    <div v-if="isDragNDropEnabled" class="absolute top-2 right-2">
      <CButton v-if="isSelected" circle class="h-9 w-9 bg-primary5" secondary type="default">
        <template #icon>
          <CIcon class="ml-[2px] color-primary" icon="grip-dots-vertical" size="12" />
        </template>
      </CButton>

      <CButton
        v-else
        circle
        class="h-9 w-9"
        :class="{
          'bg-base !focus:bg-base !active:bg-base': !isSelected && !isAdded,
          'bg-primary5': isSelected,
          'bg-error2 !focus:bg-error2 !active:bg-error2 ': isAdded,
        }"
        :disabled="isItemDisabled(item as SelectedFile | SelectedFolder | SelectedPage)"
        secondary
        type="default"
        @click.stop="handleItemClick(item as ContentSelectorSelectedItem)"
      >
        <template #icon>
          <CIcon v-if="isAdded" class="ml-[2px] color-error" icon="times" size="12" />
          <CIcon v-else class="ml-[2px] color-primary" icon="plus" size="12" />
        </template>
      </CButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { inject, computed, type ToRefs, type Ref, type ComputedRef, ref } from 'vue'

import type { AppProps } from '@content-selector/appTypes'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import tooltipDirective from '@lib/directives/tooltip'
import { isPitcherOrIosDevice } from '@lib/util/userAgents'
import type { File, FileContentTypeEnum, User } from '@openapi'
import { isShareable as isShareableFn } from '@content-selector/util/file'
import type {
  ContentSelectorSelectedItem,
  FolderFileWithFolderId,
  RecentFileWithFavoriteId,
  SelectedFile,
  SelectedFolder,
  SelectedPage,
} from '@content-selector/stores/app'
import type { FolderFolder } from '@openapi'
import { useStore } from '@content-selector/composables/useStore'
import type { PitcherInfo } from '@lib/types/app'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = withDefaults(
  defineProps<{
    item: {
      type?: 'folder' | 'file' | 'page' | 'asset'
      content_type?: FileContentTypeEnum | null
      content_url?: string | null
      thumbnail_url?: string | null
      name?: string
      file?: { id: File['id']; name?: File['name'] }
      metadata?: Record<string, any>
      page_index?: number
      permissions?: File['permissions']
      id: string
      content_extension?: string | null
      created_by_user_id?: User['id']
    }
    isSelected?: boolean
    isDeleteDisabled?: boolean
    deleteDisabledTooltip?: string
    movable?: boolean
    selectedIds?: (number | string)[]
  }>(),
  {
    item: () => ({ id: Math.random().toString() }),
    isSelected: false,
    isDeleteDisabled: false,
    deleteDisabledTooltip: '',
    movable: false,
    selectedIds: () => [],
  },
)
const myUser = inject<Ref<User | null>>(
  'myUser',
  computed(() => null),
)
const pitcherInfo = inject<Ref<PitcherInfo | null>>(
  'pitcherInfo',
  computed(() => null),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const emit = defineEmits([
  'itemSelected',
  'itemDeselected',
  'itemClicked',
  'itemExpanded',
  'itemDeleted',
  'dragging',
  'dragOver',
  'move',
])
const appProps = inject<ToRefs<AppProps>>('appProps')
const vTooltip = tooltipDirective

const { isItemDisabled, isDragNDropEnabled, handleItemClick, addedItems } = useStore()
const themeVars = useThemeVars()
const { t } = useI18n()

const isAdded = computed(() => addedItems.value.find((item) => item.id === props.item.id))
const draggedOver = ref(false)
const dragData = ref<{ itemIds: (string | number)[]; ghostElem: HTMLElement | null }>({
  itemIds: [],
  ghostElem: null,
})

const selected = computed(() => props.item.id && props.selectedIds.includes(props.item.id))

const isShareable = computed(
  () =>
    'type' in props.item &&
    isShareableFn(
      props.item as
        | SelectedFile
        | SelectedFolder
        | SelectedPage
        | RecentFileWithFavoriteId
        | FolderFileWithFolderId
        | (FolderFolder & { folder_id?: string; type: 'folder' }),
    ),
)

const isDisabled = computed(
  () =>
    'type' in props.item &&
    isItemDisabled(
      props.item as
        | SelectedFile
        | SelectedFolder
        | SelectedPage
        | RecentFileWithFavoriteId
        | FolderFileWithFolderId
        | (FolderFolder & { folder_id?: string; type: 'folder' }),
    ),
)

// Allow deletion if user is the owner OR if user is an admin
const canDelete = computed(() => {
  const isOwner = props.item.created_by_user_id === myUser.value?.id
  const isAdmin = pitcherInfo.value?.isAnyTypeOfAdmin || pitcherInfo.value?.isOrgAdmin || false

  return isOwner || isAdmin
})

const onGridItemClick = () => {
  if (isDisabled.value) {
    return
  }

  return props.item.type === 'file' || props.item.type === 'page' || props.item.type === 'asset'
    ? emit(props.isSelected ? 'itemDeselected' : 'itemSelected', props.item)
    : emit('itemClicked', props.item)
}

function handleDragStart(event: DragEvent) {
  if (!props.movable) {
    event.preventDefault()
    return
  }

  if (!selected.value) {
    onGridItemClick()
  }

  event.dataTransfer?.setData('text/plain', JSON.stringify(props.selectedIds))

  const ghostElem = document.createElement('div')
  ghostElem.style.backgroundColor = themeVars.value.primary
  ghostElem.style.color = themeVars.value.base
  ghostElem.className = 'pa-3 w-12 h-6 flex justify-center items-center text-xs font-bold rounded-2'
  ghostElem.textContent = `${props.selectedIds.length}`

  document.body.appendChild(ghostElem)

  event.dataTransfer!.setDragImage(ghostElem, 0, 0)
  dragData.value = { itemIds: props.selectedIds, ghostElem }
  emit('dragging', true)
  setTimeout(() => {
    document.body.removeChild(ghostElem)
  }, 0)
}

function handleDragEnd() {
  dragData.value = { itemIds: [], ghostElem: null }
  emit('dragging', false)
}

function handleDragOver(event: DragEvent) {
  event.preventDefault()
  event.dataTransfer?.setData('text/plain', JSON.stringify(props.selectedIds))

  emit('dragOver', event)
  if (draggedOver.value) return

  draggedOver.value = true
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  draggedOver.value = false
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  const itemIds = JSON.parse(event.dataTransfer?.getData('text/plain') || '[]')
  emit('move', { targetItemId: props.item.id, itemIds })
  draggedOver.value = false
}
</script>

<style scoped>
.grid-item--base {
  background-color: v-bind('themeVars.base');
}

.grid-item--selected {
  background-color: v-bind('themeVars.primary4');
}
</style>
